import React, { useEffect } from 'react'
import { CacheLabel } from '@/components/CacheLabel'
import { FormCheckboxField, FormReadonlyField, FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { ClientLeverageType } from 'api/Clients/Types/ClientLeverageType'
import { ClientPlatformType } from 'api/Clients/Types/ClientPlatformType'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { AccountGroupBasicDto } from 'api/Trading-Accounts/Types/AccountGroupDto'
import { AccountType } from 'api/Trading-Accounts/Types/AccountType'
import { CreateTradingAccount } from 'api/Trading-Accounts/Types/CreateTradingAccount'
import { IntroducingBrokerBasicDto } from 'api/Trading-Accounts/Types/IntroducingBrokerBasicDto'
import { CurrencyType, WalletDto } from 'api/Wallet/Types/Wallet'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { INTRODUCING_BROKERS_CACHE_MESSAGE } from 'utils/cache'
import { FormSubmitValues } from 'utils/formValidation'
import stringComparator from 'utils/stringComparator'

interface AddTradingAccountDetailsFormValues {
  client: string
  wallet: string
  currencyId: CurrencyType
  platformTypeId: undefined | string
  tradingAccountTypeId: undefined | string
  leverageTypeId: undefined | string
  introducingBroker: { id: string; name: string } | undefined
  receivesReports: boolean
  accountGroupId?: string
}

const AddTradingAccountDetailsFormUI: React.FC<
  FormikProps<AddTradingAccountDetailsFormValues> & OuterProps
> = (props) => {
  const {
    isSubmitting,
    values,
    errors,
    getAccountGroupsOptions,
    onChangePlatformType,
    setFieldValue,
  } = props
  const { platformTypeId, tradingAccountTypeId, introducingBroker } = values

  useEffect(() => {
    if (platformTypeId && tradingAccountTypeId) {
      getAccountGroupsOptions({
        introducingBrokerId: introducingBroker?.id,
        platformTypeId,
        tradingAccountTypeId,
      })
    }
  }, [platformTypeId, tradingAccountTypeId, introducingBroker])

  return (
    <DialogContent size='sm' render={<Form />}>
      <DialogHeader>
        <DialogTitle>Add Trading Account</DialogTitle>
      </DialogHeader>
      <FormReadonlyField
        name='client'
        label='Client'
        value={values.client}
        dataTest='add-trading-account-client-field'
      />
      <FormReadonlyField
        name='wallet'
        label='Wallet'
        value={values.wallet}
        dataTest='add-trading-account-client-field'
      />
      <FormSelectField
        name='platformTypeId'
        label='Platform Type'
        value={values.platformTypeId}
        disabled={(props.platformTypes?.length ?? 0) < 2}
        dataTest='add-trading-account-platformTypeId-field'
        options={(props.platformTypes || []).map((platformType) => ({
          id: platformType.id.toString(),
          name: platformType.name,
        }))}
        error={errors.platformTypeId}
        onChange={(platformId) => {
          setFieldValue('platformTypeId', platformId.toString())
          onChangePlatformType(Number(platformId))
        }}
      />
      <FormSelectField
        name='tradingAccountTypeId'
        label='Trading Account Type'
        value={values.tradingAccountTypeId}
        dataTest='add-trading-account-tradingAccountTypeId-field'
        options={props.tradingAccountTypes.map((tradingAccountType) => ({
          id: tradingAccountType.id.toString(),
          name: tradingAccountType.name,
        }))}
        error={errors.tradingAccountTypeId}
        allowClear
        onChange={(value) => setFieldValue('tradingAccountTypeId', value?.toString())}
      />
      <FormReadonlyField
        name='currencyId'
        label='Currency'
        value={values.currencyId}
        dataTest='add-trading-account-currency-field'
      />
      <FormSelectField
        name='leverageTypeId'
        label='Leverage'
        value={values.leverageTypeId}
        dataTest='add-trading-account-tradingAccountLeverageTypeId-field'
        options={props.leverageType.map((leverageType) => ({
          id: leverageType.id.toString(),
          name: leverageType.name,
        }))}
        error={errors.leverageTypeId}
        allowClear
        onChange={(value) => setFieldValue('leverageTypeId', value?.toString())}
      />
      <FormSelectField
        name='introducingBroker.id'
        label={
          <CacheLabel
            label='Introducing Broker'
            message={INTRODUCING_BROKERS_CACHE_MESSAGE}
            ariaLabel='Introducing Broker cache information'
          />
        }
        value={values.introducingBroker?.id}
        dataTest='add-trading-account-tradingAccountIntroducingBrokerId-field'
        options={(props.introducingBrokers || []).map((ib) => ({
          id: ib.id,
          name: ib.name,
        }))}
        allowClear
        showSearch
        onChange={(value) => {
          setFieldValue(
            'introducingBroker',
            props.introducingBrokers.find((ib) => ib.id === value)
          )
        }}
      />

      <FormSelectField
        name='accountGroupId'
        label='Account Group'
        placeholder='Default'
        value={values.accountGroupId}
        options={[...props.accountGroups]
          .sort((f1, f2) => stringComparator(f1.name, f2.name))
          .map((accountGroup) => ({
            id: accountGroup.id,
            name: accountGroup.name,
          }))}
        onChange={(value) => setFieldValue('accountGroupId', value.toString())}
      />
      <FormCheckboxField
        name='receivesReports'
        label='Receive reports'
        dataTest='add-trading-account-tradingAccountReceiveReports-field'
      />
      <DialogFooter>
        <Button disabled={isSubmitting} type='submit'>
          Submit
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}

function dataToSubmit(
  clientId: string,
  walletId: string,
  values: AddTradingAccountDetailsFormValues
): FormSubmitValues<CreateTradingAccount> {
  return {
    clientId,
    walletId,
    introducingBrokerId: values.introducingBroker?.id || null,
    currencyId: values.currencyId,
    leverageTypeId: parseInt(values.leverageTypeId as string, 10),
    platformTypeId: parseInt(values.platformTypeId as string, 10),
    receivesReports: values.receivesReports,
    tradingAccountTypeId: parseInt(values.tradingAccountTypeId as string, 10),
    accountGroupId: values.accountGroupId || null,
  }
}

interface OuterProps {
  client: ClientDetailedDto
  wallet: WalletDto
  tradingAccountTypes: AccountType[]
  introducingBrokers: IntroducingBrokerBasicDto[]
  leverageType: ClientLeverageType[]
  platformTypes?: ClientPlatformType[]
  permissions: PermissionDto[]
  accountGroups: AccountGroupBasicDto[]
  onChangePlatformType: (platformTypeId: number) => void

  onSubmit(values: FormSubmitValues<CreateTradingAccount>): Promise<void>

  getAccountGroupsOptions({
    platformTypeId,
    tradingAccountTypeId,
    introducingBrokerId,
  }: {
    platformTypeId: string
    tradingAccountTypeId: string
    introducingBrokerId?: string
  }): void
}

export const AddTradingAccountDetailsForm = withFormik<
  OuterProps,
  AddTradingAccountDetailsFormValues
>({
  mapPropsToValues: ({ client, wallet, tradingAccountTypes, platformTypes }) => {
    return {
      client: `${client.firstname} ${client.lastname}`,
      wallet: wallet.name,
      platformTypeId: platformTypes?.[0] ? platformTypes[0].id.toString() : undefined,
      currencyId: wallet.currency.id,
      leverageTypeId: undefined,
      introducingBroker: client.introducingBroker || undefined,
      receivesReports: false,
      tradingAccountTypeId: tradingAccountTypes[0]
        ? tradingAccountTypes[0].id.toString()
        : undefined,
      accountGroupId: '',
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(props.client.id, props.wallet.id, values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<AddTradingAccountDetailsFormValues> = {}
    if (!values.leverageTypeId) {
      errors.leverageTypeId = 'required'
    }
    if (!values.platformTypeId) {
      errors.platformTypeId = 'required'
    }
    if (!values.tradingAccountTypeId) {
      errors.tradingAccountTypeId = 'required'
    }
    return errors
  },
  enableReinitialize: false,
})(AddTradingAccountDetailsFormUI)
